import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { debounce } from 'lodash'
import { isCancel } from 'axios'
import { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { CalendarFilterEnum } from 'shared/ui/CalendarDropdown/calendarEnum'
import type { ActionItem, ICardFilter } from 'shared/ui'
import { TableStore } from 'shared/ui/TableNew'
import { logger, DayjsFormats } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { type IDonutChartDataItem, type IDonutChartBar } from 'shared/ui/DonutChart'
import { RequestCancelAxios } from 'shared/api/lib'
import { SurveyContactRow } from 'entities/Surveys/model/Table/SurveyContactRow'
import { tableLimitStore } from 'entities/TableLimit'
import { SurveysAnalyticsApi } from 'entities/Analytics/api/surveysAnalytics'
import {
  type IParamsAnalyticsSurvey,
  type IParamsAnalyticsSurveyContacts,
  type IResponseAnalyticsSurvey,
  type ISurveyContactsFilterCustom,
} from 'entities/Analytics/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type SurveyFull } from 'entities/Surveys/model/SurveyFull'
import type { IOpenContactsDetails } from 'widgets/ContactsDetails'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import { ContactsTagsModalStore } from 'widgets/ContactsTagsModal'
import { ExportAnalyticsModalStore, getExportReportAction } from 'widgets/ExportAnalyticsModal'
import { getSurveyViewActions } from 'pages/surveys/ui/actions'
import { toggleSurveyActive } from 'pages/surveys/ui/actions/toggleActive/toggleActive'

export class ViewPageStore {
  readonly calendarFilterStore: CalendarFilterStore
  private _activeFilter: ISurveyContactsFilterCustom = 'all'
  readonly tableStore: TableStore<SurveyContactRow>
  readonly contactsTagsModalStore: ContactsTagsModalStore
  private readonly _exportModalStore: ExportAnalyticsModalStore<IParamsAnalyticsSurvey>
  private _search = ''
  private _itemsMap = new Map<number, SurveyContactRow>()
  private _statistics: IResponseAnalyticsSurvey['statistics'] | null = null
  private _disabled = true
  private _cancelRequestStatistics = new RequestCancelAxios()
  private _cancelRequestExecutions = new RequestCancelAxios()
  private _disposeParams: IReactionDisposer | null = null
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _debounceFetchAnalyticsExecutions: ReturnType<typeof debounce>
  private _contactsDetailsManageStore: ContactsDetailsManageStore

  constructor(private _survey: SurveyFull, private _entities: IEntitiesStore) {
    this.calendarFilterStore = new CalendarFilterStore(this._survey.created_at)
    this.calendarFilterStore.setSelectedCalendarFilter(CalendarFilterEnum.last30days)
    this.calendarFilterStore.setParamsDate()
    this.tableStore = new TableStore<SurveyContactRow>({
      key: 'survey-contacts',
      element: 'contact',
      page: 1,
      limit: 10,
      sortOrder: 'asc',
      getLimit: () => tableLimitStore.getLimit('survey-contacts-table'),
      setLimit: (value) => tableLimitStore.setLimit('survey-contacts-table', value),
      onSort: () => {
        this.tableStore.setPage(1)
      },
    })
    this._debounceFetchAnalyticsExecutions = debounce(this._fetchAnalyticsExecutions, 500)
    this.contactsTagsModalStore = new ContactsTagsModalStore(this._entities)
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(this._entities)
    void this._contactsDetailsManageStore.init()
    this._exportModalStore = new ExportAnalyticsModalStore<IParamsAnalyticsSurvey>((params) =>
      SurveysAnalyticsApi.exportAnalyticsSurveyById(this._survey.id, params)
    )

    makeAutoObservable(this)
  }

  get onOpenContactsDetails(): IOpenContactsDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      open: (data) => {
        this._contactsDetailsManageStore.onOpenContactDetails(
          {
            contactId: data.contactId,
            inboxId: this._survey.inboxId,
          },
          'survey',
          true
        )
      },
    }
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }

  get search() {
    return this._search
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get selectedContactIds() {
    const contactIds = this.tableStore.selectedRows
      .map((row) => row.contact_id)
      .filter((contactId): contactId is number => contactId !== null)

    return Array.from(new Set(contactIds))
  }

  get survey() {
    return this._survey
  }

  get activeFilter() {
    return this._activeFilter
  }

  get payloadActiveFilter() {
    if (this._activeFilter === 'all') return null

    return this._activeFilter
  }

  private get _isExportDisabled() {
    return !this.statistics.total_sent
  }

  private get _exportAction(): ActionItem {
    return getExportReportAction({
      disabled: this._isExportDisabled,
      onExport: this._openExportModal,
    })
  }

  get actions() {
    return getSurveyViewActions(this._survey, this._exportAction)
  }

  get dataStatus() {
    return {
      id: this._survey.id,
      status: this._survey.status,
    }
  }

  get statistics(): IResponseAnalyticsSurvey['statistics'] {
    if (!this._statistics) {
      return {
        total_sent: 0,
        valid_replies: 0,
        valid_replies_percent: 0,
        invalid_replies: 0,
        invalid_replies_percent: 0,
        undelivered: 0,
        undelivered_percent: 0,
        expired: 0,
        expired_percent: 0,
        nps_score: 0,
        promoters_count: 0,
        promoters_percent: 0,
        passives_count: 0,
        passives_percent: 0,
        detractors_count: 0,
        detractors_percent: 0,
      }
    }

    return this._statistics
  }

  get dateFrom() {
    const from = this.calendarFilterStore.sendDate?.from

    return from ? uiStore.dayjsWithoutTimezone(from).format(DayjsFormats.timestampServerDate) : null
  }

  get dateTo() {
    const to = this.calendarFilterStore.sendDate?.to

    return to ? uiStore.dayjsWithoutTimezone(to).format(DayjsFormats.timestampServerDate) : null
  }

  get paramsWithoutPage(): Omit<IParamsAnalyticsSurveyContacts, 'page'> {
    return {
      filter: this.payloadActiveFilter,
      search: this._search,
      per_page: this.tableStore.limit,
      sort_dir: this.tableStore.sortOrder,
      date_from: this.dateFrom,
      date_to: this.dateTo,
    }
  }

  get params(): IParamsAnalyticsSurveyContacts {
    return {
      page: this.tableStore.page,
      ...this.paramsWithoutPage,
    }
  }

  get statisticsBlocks(): ICardFilter<ISurveyContactsFilterCustom>[] {
    const stats = this.statistics

    return [
      {
        key: 'all',
        title: 'Total sent',
        amount: stats.total_sent,
        disabled: this.disabled,
        tooltipProps: {
          width: 135,
          placement: 'top',
          label: (
            <>
              Contacts who got
              <br />
              the survey
            </>
          ),
        },
      },
      {
        key: 'valid',
        title: 'Valid replies',
        amount: stats.valid_replies,
        percentage: stats.valid_replies_percent,
        disabled: this.disabled,
        tooltipProps: {
          width: 135,
          placement: 'top',
          label: (
            <>
              Contacts who gave
              <br />
              valid answers
            </>
          ),
        },
      },
      {
        key: 'undelivered',
        title: 'Undelivered',
        amount: stats.undelivered,
        percentage: stats.undelivered_percent,
        disabled: this.disabled,
        tooltipProps: {
          width: 199,
          placement: 'top',
          label: (
            <>
              Contacts who didn’t received
              <br />
              the message
            </>
          ),
        },
      },
      {
        key: 'timeout',
        title: 'Expired',
        amount: stats.expired,
        percentage: stats.expired_percent,
        disabled: this.disabled,
        tooltipProps: {
          width: 200,
          placement: 'top',
          label: (
            <>
              Contacts who didn’t respond
              <br />
              before the survey expired
            </>
          ),
        },
      },
      {
        key: 'promoter',
        title: 'Promoters (9 - 10)',
        title_color: 'var(--green-60)',
        amount: stats.promoters_count,
        percentage: stats.promoters_percent,
        disabled: this.disabled,
        tooltipProps: {
          width: 135,
          placement: 'top',
          label: (
            <>
              Contacts who
              <br />
              scored 9 or 10
            </>
          ),
        },
      },
      {
        key: 'passive',
        title: 'Passives (7-8)',
        title_color: 'var(--yellow-70)',
        amount: stats.passives_count,
        percentage: stats.passives_percent,
        disabled: this.disabled,
        tooltipProps: {
          width: 135,
          placement: 'top',
          label: (
            <>
              Contacts who
              <br />
              scored 7 or 8
            </>
          ),
        },
      },
      {
        key: 'detractor',
        title: 'Detractors (0-6)',
        title_color: 'var(--red-80)',
        amount: stats.detractors_count,
        percentage: stats.detractors_percent,
        disabled: this.disabled,
        tooltipProps: {
          width: 135,
          placement: 'top',
          label: (
            <>
              Contacts who
              <br />
              scored 0 to 6
            </>
          ),
        },
      },
    ]
  }

  get statisticsChart(): IDonutChartDataItem[] {
    const stats = this.statistics
    const promoters = stats.promoters_count
    const passives = stats.passives_count
    const detractors = stats.detractors_count
    const all = stats.total_sent || 100

    return [
      { name: 'Promoters', value: promoters, fill: 'var(--green-60)' },
      { name: 'All', value: all, fill: 'var(--gray-20)' },
      { name: 'Detractors', value: detractors, fill: 'var(--red-80)' },
      { name: 'Passives', value: passives, fill: 'var(--yellow-70)' },
    ]
  }

  get donutChartBarsItems(): IDonutChartBar[] {
    const stats = this.statistics

    return [
      {
        title: 'Promoters',
        count: stats.promoters_count,
        percent: stats.promoters_percent,
        color: 'var(--green-60)',
        tooltipProps: {
          label: (
            <>
              Promoters (9-10): Loyal customers likely
              <br />
              to recommend your product
            </>
          ),
          width: 272,
        },
      },
      {
        title: 'Passives',
        count: stats.passives_count,
        percent: stats.passives_percent,
        color: 'var(--yellow-70)',
        tooltipProps: {
          label: (
            <>
              Passives (7-8): Satisfied but unlikely to
              <br />
              actively promote
            </>
          ),
          width: 272,
        },
      },
      {
        title: 'Detractors',
        count: stats.detractors_count,
        percent: stats.detractors_percent,
        color: 'var(--red-80)',
        tooltipProps: {
          label: (
            <>
              Detractors (0-6): Unsatisfied customers
              <br />
              who may discourage others
            </>
          ),
          width: 272,
        },
      },
    ]
  }

  get disabled() {
    return this._disabled
  }

  private _reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        runInAction(() => {
          this.tableStore.setLoading(true)
          this.tableStore.setPage(1)
        })
      }
    )
  }

  private _reactionParams = () => {
    this._disposeParams?.()
    this._disposeParams = reaction(
      () => this.params,
      () => {
        this.tableStore.setLoading(true)
        this._cancelRequestExecutions.init()
        this._fetchAnalyticsStatistics()
        this._debounceFetchAnalyticsExecutions()
      }
    )
  }

  private _fetchAnalyticsStatistics = async () => {
    try {
      this._cancelRequestStatistics.init()

      const response = await SurveysAnalyticsApi.getAnalyticsSurveyById(
        this._survey.id,
        {
          date_from: this.dateFrom,
          date_to: this.dateTo,
        },
        {
          cancelToken: this._cancelRequestStatistics.token,
        }
      )

      if (!response.data.statistics.nps_score) response.data.statistics.nps_score = 0

      this._statistics = response.data.statistics
    } catch (e) {
      logger.error(e)
    }
  }

  private _fetchAnalyticsExecutions = async () => {
    try {
      this._cancelRequestExecutions.init()
      this.tableStore.setLoading(true)
      this._itemsMap.clear()

      const response = await SurveysAnalyticsApi.getAnalyticsSurveyByIdExecutions(
        this._survey.id,
        this.params,
        {
          cancelToken: this._cancelRequestExecutions.token,
        }
      )

      const items = response.data.data
      const total = response.data.meta.total

      this.tableStore.setTotal(total)

      items.forEach((item) => {
        this._itemsMap.set(
          item.execution_id,
          new SurveyContactRow({
            ...item,
            id: item.execution_id,
          })
        )
      })

      this.tableStore.setLoading(false)
    } catch (e) {
      logger.error(e)

      if (!isCancel(e)) {
        this.tableStore.setLoading(false)
      }
    }
  }

  initReactions = () => {
    this._reactionParamsWithoutPage()
    this._reactionParams()
  }

  init = async () => {
    try {
      runInAction(() => {
        this._disabled = true
      })

      await Promise.all([this._fetchAnalyticsStatistics(), this._fetchAnalyticsExecutions()])
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._disabled = false
      })
    }
  }

  dispose = () => {
    this._cancelRequestStatistics.reset()
    this._cancelRequestExecutions.reset()
    this._disposeParams?.()
    this._disposeParamsWithoutPage?.()
    this._debounceFetchAnalyticsExecutions?.cancel?.()
    this._contactsDetailsManageStore.reset()
    this._exportModalStore.dispose()
  }

  setSearch = (value: string) => {
    this._search = value
  }

  private _openExportModal = () => {
    this._exportModalStore.onOpenExport({
      date_from: this.dateFrom,
      date_to: this.dateTo,
    })
  }

  onChangeStatus = async () => {
    return toggleSurveyActive(this._survey, this._entities)
  }

  setActiveFilter = (filter: ISurveyContactsFilterCustom) => {
    this._activeFilter = filter
    this.tableStore.resetSelected()
  }

  onSuccessDelete = () => {
    this.tableStore.unselectAllIds()
    void this._fetchAnalyticsStatistics()
    void this._fetchAnalyticsExecutions()
  }

  onSuccessManageTags = () => {
    void this._fetchAnalyticsExecutions()
  }
}
